import { useScrollContext } from "@navigations/utils/ScrollContext";
import useHideNavsOnScroll from "@navigations/utils/useHideNavsOnScroll";
import { useFocusEffect, useIsFocused } from "@react-navigation/native";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
} from "react-native";
import { useTheme } from "styled-components/native";

interface UsePdpScrollStateProps {
  stickyContainerHeight: number;
}

export const usePdpScrollState = ({
  stickyContainerHeight,
}: UsePdpScrollStateProps) => {
  const theme = useTheme();
  const isFocused = useIsFocused();
  const [_, handleReset] = useScrollContext();
  const { jsScrollHandler, resetScrollState } = useHideNavsOnScroll();
  const [showProductHeaderStickySummary, setShowProductHeaderStickySummary] =
    useState(false);

  const scrollViewRef = useRef<any>(null);
  const headerProductView = useRef<any>(null);
  const headerStickySummaryViewRef = useRef<any>(null);

  const maxContentLength = 1432;

  const scrollToTop = useCallback(() => {
    scrollViewRef.current?.scrollTo?.({
      x: 0,
      y: 0,
      animated: false,
    });

    setShowProductHeaderStickySummary(false);
  }, []);

  const onViewScroll = useCallback(
    (y: number) => {
      headerProductView.current?.measure(
        (
          __: number,
          ___: number,
          ____: number,
          _____: number,
          ______: number,
          headerProductViewPosition: number
        ) => {
          headerStickySummaryViewRef.current?.measure(
            (
              _______: number,
              ________: number,
              _________: number,
              __________: number,
              ___________: number,
              headerStickySummaryViewPosition: number
            ) => {
              if (
                headerProductViewPosition -
                  headerStickySummaryViewPosition -
                  20 <=
                  stickyContainerHeight &&
                y !== 0
              ) {
                setShowProductHeaderStickySummary(true);
              } else {
                setShowProductHeaderStickySummary(false);
              }
            }
          );
        }
      );
    },
    [stickyContainerHeight]
  );

  const onScrollHandler = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      if (theme.isLargeScreen) jsScrollHandler(event);
      onViewScroll(event.nativeEvent.contentOffset.y);
    },
    [
      stickyContainerHeight,
      headerProductView.current,
      headerStickySummaryViewRef.current,
    ]
  );

  const onScroll = isFocused ? onScrollHandler : undefined;

  useFocusEffect(
    useCallback(() => {
      const isWeb = Platform.OS === "web";
      const isMobileWeb = isWeb && theme.isMobile;
      if (!isWeb || isMobileWeb) {
        handleReset();
        resetScrollState();
      }
    }, [theme.isMobile])
  );

  useEffect(() => {
    if (isFocused && theme.isMobile) {
      scrollToTop();
    }
  }, [isFocused, theme.isMobile, scrollToTop]);

  return {
    // state
    showProductHeaderStickySummary,
    setShowProductHeaderStickySummary,

    // refs
    scrollViewRef,
    headerProductView,
    headerStickySummaryViewRef,

    // constants
    maxContentLength,

    // handlers
    onScroll,
    scrollToTop,
  };
};
